import type {
  IStoreInviteInvitedPerson,
  IStoreInviteManualMember,
  IStoreInviteMember,
} from '@/common/interfaces/store-invites.interface';
import { getTextBlastSentCategory } from '@/constants/MixpanelEventData';
import {
  STORE_INVITES_REMINDER_COUNT_PLURAL_SUFFIX,
  STORE_INVITES_REMINDER_COUNT_SINGULAR,
  STORE_INVITES_REMINDER_COUNTDOWN_SOON,
  STORE_INVITES_REMINDER_HOUR_AGO_TEXT,
  STORE_INVITES_REMINDER_HOURS_AGO_SUFFIX,
  STORE_INVITES_REMINDER_JUST_SENT_TEXT,
  STORE_INVITES_REMINDER_JUST_SENT_WINDOW_MS,
  STORE_INVITES_REMINDER_MEMBER_NOUN,
  STORE_INVITES_REMINDER_MEMBERS_NOUN,
  STORE_INVITES_REMINDER_RESEND_WINDOW_MS,
  STORE_INVITES_REMINDER_SENDING_PREFIX,
  STORE_INVITES_REMINDER_SENT_HOURS_AGO_PREFIX,
  STORE_INVITES_REMINDER_SENT_PREFIX,
  STORE_INVITES_UNNAMED_MEMBER_TEXT,
  type IStoreInviteAudienceNouns,
} from '@/constants/StoreInvites';
import {
  getGroupOrderStoreCloseTimestamp,
  getOrderExpirationTime,
  isSameStoreCalendarDay,
} from '@/utilities/OrderUtils';

/** Stable key for a member row; the API may omit `id` for members without an account. */
export const getStoreInviteMemberKey = (member: IStoreInviteMember, index: number): string =>
  String(member.id ?? member.email ?? `${member.name ?? ''}-${index}`);

export const getStoreInviteMemberName = (
  member: IStoreInviteMember,
  unnamedLabel = STORE_INVITES_UNNAMED_MEMBER_TEXT
): string => member.name ?? member.email ?? unnamedLabel;

/**
 * Recipients are opt-out: everyone in the group is invited unless explicitly unchecked, so
 * searching or paging the list never silently drops members the user never saw.
 * `manualRecipientCount` must already exclude numbers that match a selected member or
 * someone already invited.
 */
export const getStoreInviteRecipientCount = (
  groupCount: number,
  deselectedCount: number,
  manualRecipientCount: number
): number => Math.max(groupCount - deselectedCount, 0) + manualRecipientCount;

/** Digits only, capped at a US 10-digit mobile number. */
export const getStoreInvitePhoneDigits = (phone: string): string => {
  const digits = phone.replace(/\D/g, '');

  if (digits.length >= 11 && digits.startsWith('1')) {
    return digits.slice(1, 11);
  }

  return digits.slice(0, 10);
};

/** 10-digit key when the value is a US number; otherwise the original id. */
export const getStoreInvitePersonPhoneKey = (value: string | number | null | undefined): string => {
  const raw = String(value ?? '');
  const digits = getStoreInvitePhoneDigits(raw);

  return digits.length === 10 ? digits : raw;
};

export const collectStoreInvitePhoneDigits = (
  values: Array<string | number | null | undefined> | undefined
): Set<string> => {
  const digits = new Set<string>();

  (values ?? []).forEach((value) => {
    const key = getStoreInvitePhoneDigits(String(value ?? ''));
    if (key.length === 10) {
      digits.add(key);
    }
  });

  return digits;
};

export const isStoreInvitePhoneOccupied = (
  phone: string,
  occupiedPhoneDigits: Iterable<string>
): boolean => {
  const digits = getStoreInvitePhoneDigits(phone);
  if (digits.length !== 10) {
    return false;
  }

  const occupied =
    occupiedPhoneDigits instanceof Set ? occupiedPhoneDigits : new Set(occupiedPhoneDigits);

  return occupied.has(digits);
};

/**
 * Phones that already have a slot in this send: selected group members plus people who
 * already received an invite. Unchecked members are omitted so a CSV can add them back.
 */
export const getStoreInviteSelectedOccupiedPhoneDigits = (
  occupiedPhones: Array<string | null | undefined> | undefined,
  invitedPeople: Array<{ id: string | number }>,
  members: IStoreInviteMember[],
  deselectedMemberIds: string[]
): Set<string> => {
  const occupied = collectStoreInvitePhoneDigits([
    ...(occupiedPhones ?? []),
    ...invitedPeople.map((person) => person.id),
    ...members.map((member) => member.phone),
  ]);
  const deselectedIds = new Set(deselectedMemberIds);

  members.forEach((member) => {
    if (!deselectedIds.has(String(member.id))) {
      return;
    }

    const key = getStoreInvitePhoneDigits(String(member.phone ?? ''));
    if (key.length === 10) {
      occupied.delete(key);
    }
  });

  return occupied;
};

/** Manual contacts that are still selected and are not already covered by another source. */
export const filterStoreInviteUniqueManualRecipients = (
  manuals: IStoreInviteManualMember[],
  deselectedMemberIds: string[],
  occupiedPhoneDigits: Iterable<string>
): IStoreInviteManualMember[] => {
  const deselectedIds = new Set(deselectedMemberIds);
  const occupied =
    occupiedPhoneDigits instanceof Set ? occupiedPhoneDigits : new Set(occupiedPhoneDigits);

  return manuals.filter((member) => {
    if (deselectedIds.has(member.id)) {
      return false;
    }

    const digits = getStoreInvitePhoneDigits(member.phone || member.id);

    return digits.length !== 10 || !occupied.has(digits);
  });
};

export const getStoreInviteSendRecipientCount = (result: unknown): number | null => {
  if (!result || typeof result !== 'object') {
    return null;
  }

  const root = result as Record<string, unknown>;
  const nestedData =
    root.data && typeof root.data === 'object' ? (root.data as Record<string, unknown>) : root;
  const campaign =
    nestedData.campaign && typeof nestedData.campaign === 'object'
      ? (nestedData.campaign as Record<string, unknown>)
      : nestedData;
  const rawCount =
    campaign.total_recipients ?? campaign.totalRecipients ?? nestedData.total_recipients;
  const count = typeof rawCount === 'number' ? rawCount : Number(rawCount);

  return Number.isFinite(count) && count >= 0 ? count : null;
};

/**
 * Reads the API error message from an RTK Query unwrap rejection so HQ can
 * show the server copy (e.g. profanity) instead of a generic send failure.
 */
export const getStoreInviteRequestErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return fallback;
  }

  const { data } = error as { data: unknown };
  if (typeof data === 'string' && data.trim()) {
    return data;
  }

  if (!data || typeof data !== 'object' || !('message' in data)) {
    return fallback;
  }

  const { message } = data as { message: unknown };
  if (typeof message !== 'string' || !message.trim()) {
    return fallback;
  }

  return message;
};

/** Formats as the user types: (555) 123-4567 */
export const formatStoreInvitePhone = (raw: string): string => {
  const digits = getStoreInvitePhoneDigits(raw);

  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const isValidStoreInvitePhone = (phone: string): boolean =>
  getStoreInvitePhoneDigits(phone).length === 10;

interface IStoreInviteMembersLoadState {
  isFetching: boolean;
  count?: number;
  hasData?: boolean;
}

/**
 * People still available to text in this audience after removing already-invited numbers.
 * Falls back to `count` when occupied phones were not returned.
 */
export const getStoreInviteRemainingAudienceCount = (
  count: number,
  occupiedPhones: Array<string | null | undefined> | undefined,
  invitedPhoneDigits: Iterable<string>,
  members: Array<{ phone?: string | null }> = []
): number => {
  const invited =
    invitedPhoneDigits instanceof Set ? invitedPhoneDigits : new Set(invitedPhoneDigits);
  const occupiedPhoneList = occupiedPhones ?? [];
  const phones =
    occupiedPhoneList.length > 0
      ? [...occupiedPhoneList, ...members.map((member) => member.phone)]
      : [];

  if (!phones.length) {
    return Math.max(count, 0);
  }

  const remaining = new Set<string>();
  phones.forEach((phone) => {
    const digits = getStoreInvitePhoneDigits(String(phone ?? ''));
    if (digits.length === 10 && !invited.has(digits)) {
      remaining.add(digits);
    }
  });

  return remaining.size;
};

/**
 * True when every audience has no remaining sendable members. Stale cached counts are
 * ignored when occupied phones are covered by people who already received an invite.
 */
export const hasNoStoreInviteApiMembers = (queries: IStoreInviteMembersLoadState[]): boolean =>
  queries.length > 0 &&
  queries.every((query) => Number(query.count ?? 0) === 0) &&
  queries.every((query) => query.hasData || !query.isFetching);

/** True when another API page should load after the current members are on screen. */
export const hasMoreStoreInviteMembers = (
  loadedCount: number,
  totalCount: number,
  lastPageCount: number,
  pageSize: number
): boolean => loadedCount < totalCount || lastPageCount === pageSize;

/** Appends the next page and skips members already present by id or email. */
export const mergeStoreInviteMemberPages = (
  currentMembers: IStoreInviteMember[],
  incomingMembers: IStoreInviteMember[]
): IStoreInviteMember[] => {
  const seen = new Set(
    currentMembers.map((member) => String(member.id ?? member.email ?? '')).filter(Boolean)
  );
  const nextMembers = [...currentMembers];

  incomingMembers.forEach((member) => {
    const key = String(member.id ?? member.email ?? '');
    if (key && seen.has(key)) {
      return;
    }

    nextMembers.push(member);
    if (key) {
      seen.add(key);
    }
  });

  return nextMembers;
};

export const getStoreInviteSelectedMembersLabel = (
  selectedCount: number,
  singularLabel: string,
  pluralLabel: string
): string => `${selectedCount} ${selectedCount === 1 ? singularLabel : pluralLabel}`;

/** Italic countdown line shown above the reminder message. */
export const getStoreInviteReminderCountdownLine = (
  closeDate: string | undefined,
  fallback: string
): string => {
  if (!closeDate) {
    return fallback;
  }

  const closeMs = getGroupOrderStoreCloseTimestamp(closeDate);
  if (closeMs == null) {
    return fallback;
  }

  const remainingDays = Number.parseInt(getOrderExpirationTime(closeDate).orderDueDays, 10);

  if (remainingDays <= 0) {
    return STORE_INVITES_REMINDER_COUNTDOWN_SOON;
  }

  return remainingDays === 1 ? 'Store closes in 1 day' : `Store closes in ${remainingDays} days`;
};

/** Mixpanel Category for Text Blast Sent: Live Store on launch day, otherwise days left until close. */
export const resolveStoreInviteTextBlastCategory = (
  storeLiveAt?: string,
  storeCloseAt?: string
): string => {
  const remainingDays =
    storeCloseAt && getGroupOrderStoreCloseTimestamp(storeCloseAt) != null
      ? Number.parseInt(getOrderExpirationTime(storeCloseAt).orderDueDays, 10)
      : undefined;

  return getTextBlastSentCategory({
    isLaunchDay: isSameStoreCalendarDay(storeLiveAt),
    remainingDays,
  });
};

/** Keeps previously invited people and appends new ones without duplicating numbers. */
export const mergeStoreInviteInvitedPeople = (
  currentPeople: IStoreInviteInvitedPerson[],
  nextPeople: IStoreInviteInvitedPerson[]
): IStoreInviteInvitedPerson[] => {
  const seenIds = new Set(currentPeople.map((person) => getStoreInvitePersonPhoneKey(person.id)));
  const uniqueNext = nextPeople.filter((person) => {
    const key = getStoreInvitePersonPhoneKey(person.id);
    if (seenIds.has(key)) {
      return false;
    }

    seenIds.add(key);
    return true;
  });

  if (uniqueNext.length === 0) {
    return currentPeople;
  }

  return [
    ...currentPeople,
    ...uniqueNext.map((person) => ({
      ...person,
      reminder_send_count: person.reminder_send_count ?? 0,
    })),
  ];
};

/** Adds one reminder to everyone currently on the invited list after HQ sends. */
export const incrementStoreInvitePeopleReminderCounts = (
  people: IStoreInviteInvitedPerson[]
): IStoreInviteInvitedPerson[] =>
  people.map((person) => ({
    ...person,
    reminder_send_count: (person.reminder_send_count ?? 0) + 1,
  }));

/** Manual members shown in the full list; newest stay first and search matches name. */
export const getVisibleStoreInviteManualMembers = (
  members: IStoreInviteManualMember[],
  search: string
): IStoreInviteManualMember[] => {
  const query = search.trim().toLowerCase();

  if (!query) {
    return members;
  }

  return members.filter((member) => member.name.toLowerCase().includes(query));
};

/** Unique extra manuals first, then loaded API members that were not unchecked. */
export const getStoreInvitePeopleFromSend = (
  manualMembers: IStoreInviteManualMember[],
  apiMembers: IStoreInviteMember[],
  deselectedMemberIds: string[],
  unnamedLabel = STORE_INVITES_UNNAMED_MEMBER_TEXT,
  occupiedPhoneDigits: Iterable<string> = []
): IStoreInviteInvitedPerson[] => {
  const deselectedIds = new Set(deselectedMemberIds);
  const uniqueManuals = filterStoreInviteUniqueManualRecipients(
    manualMembers,
    deselectedMemberIds,
    occupiedPhoneDigits
  );
  const seenPhones = collectStoreInvitePhoneDigits(
    uniqueManuals.map((member) => member.phone || member.id)
  );
  const manualPeople = uniqueManuals.map((member) => ({
    id: member.id,
    name: member.name,
    reminder_send_count: 0,
  }));
  const apiPeople = apiMembers.reduce<IStoreInviteInvitedPerson[]>((people, member, index) => {
    const memberKey = getStoreInviteMemberKey(member, index);
    if (deselectedIds.has(memberKey) || deselectedIds.has(String(member.id ?? ''))) {
      return people;
    }

    const digits = getStoreInvitePhoneDigits(String(member.phone ?? ''));
    if (digits.length === 10) {
      if (seenPhones.has(digits)) {
        return people;
      }
      seenPhones.add(digits);
    }

    people.push({
      id: digits.length === 10 ? digits : memberKey,
      name: getStoreInviteMemberName(member, unnamedLabel),
      reminder_send_count: 0,
    });

    return people;
  }, []);

  return [...manualPeople, ...apiPeople];
};

export const getStoreInviteReminderCountLabel = (count: number): string =>
  count === 1
    ? STORE_INVITES_REMINDER_COUNT_SINGULAR
    : `${count} ${STORE_INVITES_REMINDER_COUNT_PLURAL_SUFFIX}`;

export const getStoreInviteReminderMemberNoun = (
  count: number,
  nouns: Pick<IStoreInviteAudienceNouns, 'singular' | 'plural'> = {
    singular: STORE_INVITES_REMINDER_MEMBER_NOUN,
    plural: STORE_INVITES_REMINDER_MEMBERS_NOUN,
  }
): string => (count === 1 ? nouns.singular : nouns.plural);

export const getStoreInviteReminderSendingText = (
  count: number,
  nouns?: Pick<IStoreInviteAudienceNouns, 'singular' | 'plural'>
): string =>
  `${STORE_INVITES_REMINDER_SENDING_PREFIX} ${count} ${getStoreInviteReminderMemberNoun(
    count,
    nouns
  )}…`;

export const getStoreInviteReminderSentText = (
  count: number,
  nouns?: Pick<IStoreInviteAudienceNouns, 'singular' | 'plural'>
): string =>
  `${STORE_INVITES_REMINDER_SENT_PREFIX} ${count} ${getStoreInviteReminderMemberNoun(
    count,
    nouns
  )}`;

/** True when another send within 24 hours should ask before going out again. */
export const shouldConfirmStoreInviteReminderResend = (
  lastSentAt: number | undefined,
  now = Date.now()
): boolean =>
  lastSentAt !== undefined && now - lastSentAt < STORE_INVITES_REMINDER_RESEND_WINDOW_MS;

export const getStoreInviteResendElapsedLabel = (elapsedMs: number): string => {
  if (elapsedMs < STORE_INVITES_REMINDER_JUST_SENT_WINDOW_MS) {
    return STORE_INVITES_REMINDER_JUST_SENT_TEXT;
  }

  const hours = Math.floor(elapsedMs / STORE_INVITES_REMINDER_JUST_SENT_WINDOW_MS);

  return hours === 1
    ? `${STORE_INVITES_REMINDER_SENT_HOURS_AGO_PREFIX} ${STORE_INVITES_REMINDER_HOUR_AGO_TEXT}`
    : `${STORE_INVITES_REMINDER_SENT_HOURS_AGO_PREFIX} ${hours} ${STORE_INVITES_REMINDER_HOURS_AGO_SUFFIX}`;
};

export const createStoreInviteManualMember = (
  name: string,
  phone: string,
  batch?: { id: string; label: string }
): IStoreInviteManualMember => {
  const formattedPhone = formatStoreInvitePhone(phone);

  return {
    id: getStoreInvitePhoneDigits(formattedPhone),
    name: name.trim(),
    phone: formattedPhone,
    ...(batch ? { batchId: batch.id, batchLabel: batch.label } : {}),
  };
};
